import { create } from 'zustand';
import { AxiosError } from 'axios';
import { applicationService } from 'src/services/application';
import {
  Application,
  Plan,
  SubscriptionPlan,
} from 'src/services/interfaces/application';
import { useInsuranceStore } from './insurance';
// import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { DEFAULT_TIER } from '@features/onboarding/constants';
import { Tier } from 'src/services/interfaces/tier';
// import { FormStepID } from '@features/onboarding/enums/formSections.enum';
import { useAcceptanceChangeStore } from './acceptance-change';
import { useUnderReviewStore } from './under-review';
import { guardB2CSideEffect } from '@features/w2/store/w2-mode';
import { parseISO, isValid } from 'date-fns';

const PHQ_PROGRESS_STEP = 5;

let ensureFormDraftPromise: Promise<boolean> | null = null;

// Helper to validate and clean effectiveDate - if expired, set to null
const cleanApplicationData = (data: Application): Application => {
  if (data.phq && data.phq.effectiveDate) {
    const effectiveDate = data.phq.effectiveDate;
    try {
      const parsedDate = parseISO(effectiveDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // If the date is in the past or invalid, set to null
      if (!isValid(parsedDate) || parsedDate < today) {
        return {
          ...data,
          phq: {
            ...data.phq,
            effectiveDate: null,
          },
        };
      }
    } catch (error) {
      return {
        ...data,
        phq: {
          ...data.phq,
          effectiveDate: null,
        },
      };
    }
  }
  return data;
};

interface ApplicationState {
  signature: string | null;
  applications: Application[];
  originalApiPlans: Plan[];
  isLoading: boolean;
  isCreated: boolean;
  error: string | null;
  status?: 'created' | 'pending' | 'approved' | 'rejected';
  currentApplicationId?: number | string;
  isMainPlanRejected?: boolean;
  activeSubscriptionPlans: SubscriptionPlan[];
  activeCategories: string[];
  isAllRejected: boolean;
  currentApplicationStatus: 0 | 1;
  signedAt: string | null;
  submittedAt?: string | null;
  originalEffectiveDate: string | null;
  hasCompletedApplication: boolean;
  isNewApplicant: boolean;
  hasReachedPhq: boolean | null;
  progress: number;
  abandonedAt: string | null;
  updatedAt: string | null;
  createApplication: (data: Application) => Promise<void>;
  updateApplication: (data: Application) => Promise<void>;
  submitApplication: (
    id: number,
    data: { paymentMethodId?: string; signature?: string }
  ) => Promise<void>;
  acceptApplication: () => Promise<void>;
  rejectApplication: () => Promise<void>;
  getCurrentApplication: () => Promise<void>;
  ensureFormDraftApplication: () => Promise<boolean>;
  updateApplicationById: (
    id: number | string,
    data: Application
  ) => Promise<void>;
  setIsMainPlanRejected: (isMainPlanRejected: boolean) => void;
  setIsAllRejected: (isAllRejected: boolean) => void;
  fetchActiveSubscriptionPlans: () => Promise<void>;
  updateTierFromApplicationState: () => Promise<404 | undefined>;
  tier: string;
  acceptedTier: Tier | null;
  phqUnderReviewAt: string | null;
  phqAcceptedAt: string | null;
  phqRejectedAt: string | null;
  setAcceptedTier: (tier: Tier) => void;
  clearMedicalPlan: () => Promise<void>;
}

export const useApplicationStore = create<ApplicationState>((set, get) => ({
  signature: null,
  applications: [],
  originalApiPlans: [],
  isLoading: false,
  isCreated: false,
  tier: DEFAULT_TIER,
  acceptedTier: null,
  submittedAt: null,
  isMainPlanRejected: false,
  activeSubscriptionPlans: [],
  error: null,
  activeCategories: [],
  isAllRejected: false,
  currentApplicationStatus: 0,
  signedAt: null,
  phqUnderReviewAt: null,
  originalEffectiveDate: null,
  hasCompletedApplication: false,
  isNewApplicant: false,
  hasReachedPhq: null,
  progress: 0,
  abandonedAt: null,
  updatedAt: null,
  phqAcceptedAt: null,
  phqRejectedAt: null,

  setAcceptedTier: (tier: Tier) => {
    set({ acceptedTier: tier });
  },

  clearMedicalPlan: async () => {
    const { currentApplicationId, applications } = get();
    if (!currentApplicationId || !applications[0]) return;

    await get().updateApplicationById(currentApplicationId, {
      ...applications[0],
      plans: [],
    });
  },

  // Устанавливаем флаг, опираясь на уже загруженные activeSubscriptionPlans.
  // Никаких сетевых запросов здесь не выполняем, чтобы избежать лишних вызовов API.
  setIsMainPlanRejected: (rejected: boolean) => {
    const { activeSubscriptionPlans } = useApplicationStore.getState();
    const fullPlans = useInsuranceStore.getState().plans;

    const hasMajorMedical = (() => {
      if (activeSubscriptionPlans.length === 0) return false;
      const activeIds = activeSubscriptionPlans.map((p) => p.planId);
      return fullPlans.some(
        (plan) =>
          activeIds.includes(plan.id) && plan.category === 'Major Medical'
      );
    })();

    const isMainPlanRejectedValue = rejected && !hasMajorMedical;
    set({ isMainPlanRejected: isMainPlanRejectedValue });
  },
  setIsAllRejected: (isAllRejected: boolean) => {
    set({ isAllRejected });
  },

  fetchActiveSubscriptionPlans: async () => {
    try {
      const activeSubscriptionPlans =
        await applicationService.getActiveSubscriptionPlans();
      const fullPlans = useInsuranceStore.getState().plans;
      const activeCategories = fullPlans
        .filter((plan) =>
          activeSubscriptionPlans
            .map((item) => item.planId)
            .includes(plan?.id || 0)
        )
        .map((item) => item.category);
      const allCategories = fullPlans.map((item) => item.category);
      // isAllRejected = true только если пользователь купил планы из ВСЕХ доступных категорий
      const isAllRejected =
        allCategories.length > 0 &&
        allCategories.every((category) => activeCategories.includes(category));
      set({ activeSubscriptionPlans, activeCategories, isAllRejected });
    } catch (error) {
      console.error('Failed to fetch active subscription plans:', error);
    }
  },

  getCurrentApplication: async () => {
    set({ isLoading: true, error: null });
    let hasCompletedApplication = false;
    try {
      const applicationsList = await applicationService.getApplicationsList();
      const completedApplication = applicationsList.find(
        (application) => application.status === 1
      );
      hasCompletedApplication = !!completedApplication;
      const completedEffectiveDate =
        completedApplication?.payload?.phq?.effectiveDate || null;
      set({
        hasCompletedApplication,
        isNewApplicant: !hasCompletedApplication,
        hasReachedPhq: applicationsList.some(
          (application) => (application.progress ?? 0) >= PHQ_PROGRESS_STEP
        ),
        ...(completedEffectiveDate && {
          originalEffectiveDate: completedEffectiveDate,
        }),
      });
    } catch (listError) {
      console.error('Failed to fetch applications list:', listError);
      set({
        hasCompletedApplication: false,
        isNewApplicant: true,
        hasReachedPhq: false,
      });
    }

    try {
      const applicationData = await applicationService.getApplications();
      const {
        payload,
        id,
        status,
        signature,
        progress,
        abandonedAt,
        updatedAt,
      } = applicationData;
      set({
        currentApplicationStatus: status === 1 ? 1 : 0,
        progress: progress ?? 0,
        abandonedAt: abandonedAt ?? null,
        updatedAt: updatedAt ?? null,
      });
      if (applicationData.signature) {
        set({ signature: applicationData.signature.signatureRequestId });
      }

      if (id && status !== 1) {
        set({ currentApplicationId: id });
      } else if (status === 1) {
        set({ currentApplicationId: undefined });
      }

      // Set signedAt from signature data
      const signedAt = signature?.signedAt || null;
      const currentEffectiveDate = payload?.phq?.effectiveDate || null;
      const existingEffectiveDate =
        useApplicationStore.getState().originalEffectiveDate;
      set({
        signedAt,
        originalEffectiveDate: existingEffectiveDate || currentEffectiveDate,
      });

      if (!payload) {
        set({ isLoading: false, status: 'created' });
      } else {
        // Save original plans from API before any filtering (used for archived plans check)
        const originalApiPlans = payload.plans ? [...payload.plans] : [];
        // payload
        let activeSubscriptionPlans: SubscriptionPlan[] = [];
        if (payload.plans && payload.plans.length > 0) {
          activeSubscriptionPlans =
            await applicationService.getActiveSubscriptionPlans();
          payload.plans = payload.plans.filter(
            (plan) =>
              !activeSubscriptionPlans
                .map((item) => item.planId)
                .includes(plan.id)
          );
        }
        set({
          applications: [payload],
          originalApiPlans,
          submittedAt: applicationData?.submittedAt,
          isLoading: false,
          status: 'created',
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        set({
          applications: [],
          currentApplicationId: undefined,
          currentApplicationStatus: 0,
          isLoading: false,
          status: undefined,
          error: null,
          signedAt: null,
          submittedAt: null,
          originalEffectiveDate: null,
          progress: 0,
          abandonedAt: null,
          updatedAt: null,
        });
        return;
      }
      set({
        error: 'Failed to fetch current application.',
        isLoading: false,
        progress: 0,
        abandonedAt: null,
        updatedAt: null,
      });
    }
  },

  ensureFormDraftApplication: async () => {
    // W2 employees never bootstrap a B2C draft application (see guardB2CSideEffect).
    if (guardB2CSideEffect()) return false;
    if (ensureFormDraftPromise) {
      return ensureFormDraftPromise;
    }

    ensureFormDraftPromise = (async () => {
      try {
        const state = get();
        if (
          state.currentApplicationId &&
          state.currentApplicationStatus === 0
        ) {
          return false;
        }

        const applicationData = await applicationService.getApplications();

        if (applicationData.status !== 1) {
          if (applicationData.id) {
            set({
              currentApplicationId: applicationData.id,
              currentApplicationStatus: 0,
            });
          }
          return false;
        }

        if (!applicationData.payload) {
          return false;
        }

        await get().createApplication({
          ...applicationData.payload,
          plans: [],
        });
        await get().fetchActiveSubscriptionPlans();
        await get().getCurrentApplication();
        return true;
      } catch (error) {
        console.error('Failed to ensure form draft application:', error);
        return false;
      }
    })().finally(() => {
      ensureFormDraftPromise = null;
    });

    return ensureFormDraftPromise;
  },

  createApplication: async (data: Application) => {
    // Invariant: a W2 employee must NEVER write to B2C applications (guardB2CSideEffect).
    if (guardB2CSideEffect()) return;
    set({ isLoading: true, error: null });
    try {
      // Clean expired effectiveDate before sending
      const cleanedData = cleanApplicationData(data);
      const application =
        await applicationService.createApplication(cleanedData);
      set({
        currentApplicationId: application.id,
        currentApplicationStatus: 0,
        status: 'created',
        applications: [
          ...useApplicationStore.getState().applications,
          application,
        ],
        isLoading: false,
        isCreated: true,
      });
      // const { activeSubscriptionPlans } = useApplicationStore.getState();
      // if(useApplicationStore.getState().isMainPlanRejected || activeSubscriptionPlans.length > 0){
      //   window.location.reload();
      // }
    } catch (error) {
      set({ error: 'Failed to create application.', isLoading: false });
      throw error;
    }
  },

  updateApplication: async (data: Application) => {
    // Invariant: a W2 employee must NEVER write to B2C applications (guardB2CSideEffect).
    if (guardB2CSideEffect()) return;
    set({ isLoading: true, error: null });
    try {
      // Clean expired effectiveDate before sending
      const cleanedData = cleanApplicationData(data);
      const application =
        await applicationService.updateApplication(cleanedData);
      set({
        applications: useApplicationStore
          .getState()
          .applications.map((item) => (item === data ? application : item)),
        isLoading: false,
      });
    } catch (error) {
      set({ error: 'Failed to update application.', isLoading: false });
    }
  },

  updateApplicationById: async (id: number | string, data: Application) => {
    // Invariant: a W2 employee must NEVER write to B2C applications (guardB2CSideEffect).
    if (guardB2CSideEffect()) return;
    set({ isLoading: true, error: null });
    try {
      const isMainPlanRejected =
        useApplicationStore.getState().isMainPlanRejected;
      console.log('isMainPlanRejected', isMainPlanRejected);
      if (isMainPlanRejected) {
        const fullPlans = useInsuranceStore.getState().plans;
        const newPlans = [];
        if (fullPlans.length > 0) {
          const dataPlans = [...data.plans];
          for (const plan of dataPlans) {
            const myMajorPlan = fullPlans
              .find((p) => p.category === 'Major Medical' && p.id === plan.id)
              ?.prices.find((item) => item.id === plan.priceId);
            if (!myMajorPlan) {
              newPlans.push(plan);
            }
          }
          data.plans = dataPlans;
        }
        data.plans = newPlans;
      }
      // Clean expired effectiveDate before sending
      const cleanedData = cleanApplicationData(data);
      await applicationService.updateApplicationById(id, cleanedData);
      const activeSubscriptionPlans =
        await applicationService.getActiveSubscriptionPlans();
      set({ activeSubscriptionPlans });
    } catch (error) {
      set({ error: 'Failed to update application by id.', isLoading: false });
      throw error;
    } finally {
      await useApplicationStore.getState().getCurrentApplication();
    }
  },

  submitApplication: async (
    id: number,
    data: { paymentMethodId?: string; signature?: string }
  ) => {
    // Invariant: a W2 employee must NEVER submit a B2C application (guardB2CSideEffect).
    if (guardB2CSideEffect()) return;
    set({ isLoading: true, error: null });
    try {
      await applicationService.submitApplication(id, data);
      await useApplicationStore.getState().fetchActiveSubscriptionPlans();
      set({
        isLoading: false,
        hasCompletedApplication: true,
        isNewApplicant: false,
      });
    } catch (error) {
      set({ error: 'Failed to submit application.', isLoading: false });
    }
  },

  acceptApplication: async () => {
    // Invariant: a W2 employee must NEVER mutate a B2C application (guardB2CSideEffect).
    if (guardB2CSideEffect()) return;
    set({ isLoading: true, error: null });
    try {
      await applicationService.acceptApplication();
      set({ isLoading: false });
    } catch (error) {
      set({ error: 'Failed to accept application.', isLoading: false });
    }
  },

  rejectApplication: async () => {
    // Invariant: a W2 employee must NEVER mutate a B2C application (guardB2CSideEffect).
    if (guardB2CSideEffect()) return;
    set({ isLoading: true, error: null });
    try {
      await applicationService.rejectApplication();
      set({ isLoading: false });
    } catch (error) {
      set({ error: 'Failed to reject application.', isLoading: false });
    }
  },

  updateTierFromApplicationState: async (): Promise<404 | undefined> => {
    // W2 employees have no B2C application; skip the tier/underwriting read (this
    // is the single-seam root-cause fix for the ungated call sites, guardB2CSideEffect).
    if (guardB2CSideEffect()) return;
    try {
      const applicationState = await applicationService.applicationState();
      const { sessionLockedForHumanReview } = useUnderReviewStore.getState();

      if (sessionLockedForHumanReview) {
      } else {
        set({
          phqUnderReviewAt: applicationState.phqUnderReviewAt,
          phqAcceptedAt: applicationState.phqAcceptedAt,
          phqRejectedAt: applicationState.phqRejectedAt,
        });

        if (applicationState.acceptedTier) {
          set({
            tier: applicationState.acceptedTier,
            acceptedTier: applicationState.acceptedTier,
          });
        } else {
          set({ tier: DEFAULT_TIER, acceptedTier: null });
        }

        const isAccepted =
          !!applicationState.phqAcceptedAt && !applicationState.phqRejectedAt;
        useAcceptanceChangeStore.getState().setApplicationAccepted(isAccepted);
        if (!isAccepted) {
          useAcceptanceChangeStore.getState().setModalOpen(false);
        }
      }

      useUnderReviewStore
        .getState()
        .syncFromApplicationState(applicationState.phqUnderReviewAt ?? null);
    } catch (error) {
      console.error('Failed to update tier from application state:', error);
      set({ tier: DEFAULT_TIER, acceptedTier: null });
      if ((error as AxiosError).response?.status === 404) {
        return 404;
      }
    }
  },
}));
